import { Avatar, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import type { User } from '../api/schemas';

interface UserAvatarProps {
  user: User | null;
  size?: number;
  sx?: SxProps<Theme>;
}

function getVerificationMark(
  level: number
): { text: string; circled: boolean } | null {
  const normalizedLevel = Math.min(3, Math.max(0, Math.trunc(level)));

  if (normalizedLevel === 1) {
    return { text: '✓', circled: false };
  }

  if (normalizedLevel === 2) {
    return { text: '✓✓', circled: false };
  }

  if (normalizedLevel === 3) {
    return { text: '✓✓', circled: true };
  }

  return null;
}

const AvatarRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'avatarSize',
})<{ avatarSize: number }>(({ avatarSize }) => ({
  position: 'relative',
  width: avatarSize,
  height: avatarSize,
}));

const StyledAvatar = styled(Avatar, {
  shouldForwardProp: (prop) =>
    prop !== 'avatarSize' && prop !== 'verified',
})<{ avatarSize: number; verified: boolean }>(({ avatarSize, theme, verified }) => ({
  width: avatarSize,
  height: avatarSize,
  border: `2px solid ${
    verified
      ? (theme.vars?.palette.success.main ?? theme.palette.success.main)
      : (theme.vars?.palette.surface.panelBorder ??
        theme.palette.surface?.panelBorder ??
        theme.palette.divider)
  }`,
  background:
    theme.vars?.palette.surface.soft ??
    theme.palette.surface?.soft ??
    theme.palette.background.default,
  color: theme.vars?.palette.text.primary ?? theme.palette.text.primary,
  fontWeight: 700,
}));

const VerificationMark = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'circled',
})<{ circled: boolean }>(({ circled, theme }) => ({
  position: 'absolute',
  right: -2,
  bottom: -2,
  display: 'flex',
  minWidth: circled ? 18 : 14,
  height: circled ? 18 : 14,
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: theme.shape.surface,
  border: circled
    ? `1px solid ${theme.vars?.palette.success.main ?? theme.palette.success.main}`
    : 'none',
  paddingInline: circled ? 0 : theme.spacing(0.5),
  backgroundColor: circled
    ? (theme.vars?.palette.success.main ?? theme.palette.success.main)
    : (theme.vars?.palette.background.paper ?? theme.palette.background.paper),
  color: circled
    ? (theme.vars?.palette.success.contrastText ??
      theme.palette.success.contrastText)
    : (theme.vars?.palette.success.main ?? theme.palette.success.main),
  fontSize: circled ? 10 : 11,
  fontWeight: 800,
  lineHeight: 1,
}));

export function UserAvatar({ user, size = 40, sx }: UserAvatarProps) {
  const verificationMark = getVerificationMark(user?.kycLevel ?? 0);

  return (
    <AvatarRoot avatarSize={size} sx={sx}>
      <StyledAvatar
        src={user?.avatarUrl}
        avatarSize={size}
        verified={Boolean(user?.isVerified)}
      />
      {verificationMark ? (
        <VerificationMark circled={verificationMark.circled}>
          {verificationMark.text}
        </VerificationMark>
      ) : null}
    </AvatarRoot>
  );
}
